import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    Dimmer,
    Divider,
    Dropdown,
    Form,
    Loader,
    Message,
    Segment,
} from 'semantic-ui-react';

import AppConfig from '../../AppConfig';
import {
    MENU_GROUPS_GENERAL,
    MENU_GROUPS_REPAIR_CENTER,
    PRIMARY_HISTORY_PAIRS,
    flattenTabStatuses,
    normalizeAllowedTabStatuses,
} from '../../Menu/menuConfig';
import { useAppContext } from '../../Contexts/AppContext';

// ユーザーマスタからドロップダウン選択肢を生成する。
function buildUserOptions(userMaster) {
    return (userMaster || [])
        .filter((u) => u?.id != null && u?.userId)
        .map((u) => ({
            key: u.id,
            value: u.id,
            text: u.userName ? `${u.userName} (${u.userId})` : u.userId,
            user: u,
        }));
}

// グループ内に定義済みの Primary/History ペアがあるか判定する。
function getCombinedPair(children) {
    const childStatuses = new Set((children || []).map((c) => c.tabStatus));
    return (
        PRIMARY_HISTORY_PAIRS.find(
            ({ primary, history }) =>
                childStatuses.has(primary) && childStatuses.has(history)
        ) || null
    );
}

// ペアの許可状態を none / history / both に変換する。
function getCombinedLevel(allowedSet, primary, history) {
    const hasPrimary = allowedSet.has(primary);
    const hasHistory = allowedSet.has(history);
    if (hasPrimary && hasHistory) return 'both';
    if (!hasPrimary && hasHistory) return 'history';
    return 'none';
}

// メニューグループごとの権限セレクタ UI を構築する。
function groupToSelectors(
    groups,
    allowedSet,
    onSingleLevelChange,
    onCombinedLevelChange
) {
    return (groups || []).map((g) => {
        if (g?.children?.length) {
            const combinedPair = getCombinedPair(g.children);
            if (combinedPair) {
                const level = getCombinedLevel(
                    allowedSet,
                    combinedPair.primary,
                    combinedPair.history
                );
                const primaryLabel = g.children[0]?.name || 'Primary';
                const historyLabel = g.children[1]?.name || 'History';
                return (
                    <Segment key={g.name} padded>
                        <strong>{g.name}</strong>
                        <Divider />
                        <Form.Group>
                            <Form.Field>
                                <Checkbox
                                    radio
                                    name={`perm-${g.name}`}
                                    label="None"
                                    checked={level === 'none'}
                                    onChange={() =>
                                        onCombinedLevelChange(
                                            combinedPair.primary,
                                            combinedPair.history,
                                            'none'
                                        )
                                    }
                                />
                            </Form.Field>
                            <Form.Field>
                                <Checkbox
                                    radio
                                    name={`perm-${g.name}`}
                                    label={historyLabel}
                                    checked={level === 'history'}
                                    onChange={() =>
                                        onCombinedLevelChange(
                                            combinedPair.primary,
                                            combinedPair.history,
                                            'history'
                                        )
                                    }
                                />
                            </Form.Field>
                            <Form.Field>
                                <Checkbox
                                    radio
                                    name={`perm-${g.name}`}
                                    label={`${primaryLabel} + ${historyLabel}`}
                                    checked={level === 'both'}
                                    onChange={() =>
                                        onCombinedLevelChange(
                                            combinedPair.primary,
                                            combinedPair.history,
                                            'both'
                                        )
                                    }
                                />
                            </Form.Field>
                        </Form.Group>
                    </Segment>
                );
            }

            return g.children.map((c) => {
                const isChecked = allowedSet.has(c.tabStatus);
                return (
                    <Segment key={c.tabStatus} padded>
                        <strong>{`${g.name} - ${c.name}`}</strong>
                        <Divider />
                        <Form.Group>
                            <Form.Field>
                                <Checkbox
                                    radio
                                    name={`perm-${c.tabStatus}`}
                                    label="None"
                                    checked={!isChecked}
                                    onChange={() =>
                                        onSingleLevelChange(c.tabStatus, 'none')
                                    }
                                />
                            </Form.Field>
                            <Form.Field>
                                <Checkbox
                                    radio
                                    name={`perm-${c.tabStatus}`}
                                    label={c.name}
                                    checked={isChecked}
                                    onChange={() =>
                                        onSingleLevelChange(
                                            c.tabStatus,
                                            'allowed'
                                        )
                                    }
                                />
                            </Form.Field>
                        </Form.Group>
                    </Segment>
                );
            });
        }

        const isChecked = allowedSet.has(g.tabStatus);
        return (
            <Segment key={g.name} padded>
                <strong>{g.name}</strong>
                <Divider />
                <Form.Group>
                    <Form.Field>
                        <Checkbox
                            radio
                            name={`perm-${g.tabStatus}`}
                            label="None"
                            checked={!isChecked}
                            onChange={() =>
                                onSingleLevelChange(g.tabStatus, 'none')
                            }
                        />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            radio
                            name={`perm-${g.tabStatus}`}
                            label={g.name}
                            checked={isChecked}
                            onChange={() =>
                                onSingleLevelChange(g.tabStatus, 'allowed')
                            }
                        />
                    </Form.Field>
                </Form.Group>
            </Segment>
        );
    });
}

export default function MenuPermissionTab() {
    const { Reducer, Actions } = useAppContext();
    const [selectedUserMasterId, setSelectedUserMasterId] = useState('');
    const [allowed, setAllowed] = useState([]);
    const [saved, setSaved] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const baseGroups = useMemo(() => {
        return Reducer?.userInformation?.RepairCenterFlag
            ? MENU_GROUPS_REPAIR_CENTER
            : MENU_GROUPS_GENERAL;
    }, [Reducer?.userInformation?.RepairCenterFlag]);

    const baseTabStatuses = useMemo(
        () => flattenTabStatuses(baseGroups),
        [baseGroups]
    );

    const userOptions = useMemo(
        () => buildUserOptions(Reducer.userMaster),
        [Reducer.userMaster]
    );

    const selectedUser = useMemo(() => {
        if (selectedUserMasterId === '' || selectedUserMasterId == null)
            return null;
        return (
            (Reducer.userMaster || []).find(
                (u) => u.id === selectedUserMasterId
            ) || null
        );
    }, [Reducer.userMaster, selectedUserMasterId]);

    const isSelectedCurrentUser = useMemo(() => {
        const selectedUserId = selectedUser?.userId?.trim().toLowerCase();
        const currentUserId =
            Reducer.userInformation?.UserId?.trim().toLowerCase();
        return (
            !!selectedUserId &&
            !!currentUserId &&
            selectedUserId === currentUserId
        );
    }, [selectedUser?.userId, Reducer.userInformation?.UserId]);

    useEffect(() => {
        if (
            (selectedUserMasterId === '' || selectedUserMasterId == null) &&
            userOptions.length
        ) {
            setSelectedUserMasterId(userOptions[0].value);
        }
    }, [selectedUserMasterId, userOptions]);

    const fetchPermission = useCallback(
        (id) => {
            if (id === '' || id == null) return;
            setLoadError('');
            setLoading(true);
            const url = `${AppConfig.ApiUrlUserSetting}/menuPermission?id=${encodeURIComponent(id)}`;
            fetch(url, {
                mode: 'cors',
                credentials: 'include',
                headers: { csrftoken: JSON.stringify(Reducer.csrftoken) },
            })
                .then((res) => {
                    if (!res.ok) {
                        return res.json().then((err) => {
                            throw new Error(
                                err.Message || err.message || 'Failed to load'
                            );
                        });
                    }
                    return res.json();
                })
                .then((data) => {
                    const raw = Array.isArray(data?.allowedTabStatuses)
                        ? data.allowedTabStatuses
                        : baseTabStatuses;
                    setAllowed(normalizeAllowedTabStatuses(raw));
                    setSaved(false);
                })
                .catch((e) => setLoadError(e.message || 'Failed to load'))
                .finally(() => setLoading(false));
        },
        [Reducer.csrftoken, baseTabStatuses]
    );

    useEffect(() => {
        setSaved(false);
        setSaveError('');
        if (selectedUserMasterId === '' || selectedUserMasterId == null) return;
        fetchPermission(selectedUserMasterId);
    }, [selectedUserMasterId, fetchPermission]);

    const allowedSet = useMemo(() => new Set(allowed), [allowed]);

    // 単一タブのラジオ選択結果を許可状態へ反映する。
    function onSingleLevelChange(tabStatus, level) {
        setSaved(false);
        setSaveError('');
        setAllowed((prev) => {
            const set = new Set(prev);
            if (level === 'allowed') {
                set.add(tabStatus);
            } else {
                set.delete(tabStatus);
            }
            return normalizeAllowedTabStatuses(Array.from(set));
        });
    }

    // Primary/History ペアのラジオ選択結果を許可状態へ反映する。
    function onCombinedLevelChange(primaryKey, historyKey, level) {
        setSaved(false);
        setSaveError('');

        setAllowed((prev) => {
            const set = new Set(prev);
            if (level === 'both') {
                set.add(primaryKey);
                set.add(historyKey);
            } else if (level === 'history') {
                set.delete(primaryKey);
                set.add(historyKey);
            } else {
                set.delete(primaryKey);
                set.delete(historyKey);
            }
            return normalizeAllowedTabStatuses(Array.from(set));
        });
    }

    // ベースメニューの全タブを許可状態にする。
    function setAllAllowed() {
        setSaved(false);
        setSaveError('');
        setAllowed(baseTabStatuses);
    }

    // すべてのメニュー権限を不許可にする。
    function setNoneAllowed() {
        setSaved(false);
        setSaveError('');
        setAllowed([]);
    }

    // ログインユーザー自身の権限を編集対象へコピーする。
    function copyFromLoginUser() {
        setSaved(false);
        setSaveError('');
        const mine = Reducer.userInformation?.AllowedTabStatuses;
        const raw = Array.isArray(mine) ? mine : baseTabStatuses;
        setAllowed(normalizeAllowedTabStatuses(raw));
    }

    // 選択ユーザーの権限を保存し、自分自身を編集中ならセッション情報も同期する。
    function save() {
        if (selectedUserMasterId === '' || selectedUserMasterId == null) return;
        setSaveError('');
        setSaving(true);
        fetch(`${AppConfig.ApiUrlUserSetting}/menuPermission`, {
            mode: 'cors',
            method: 'put',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                csrftoken: JSON.stringify(Reducer.csrftoken),
            },
            body: JSON.stringify({
                id: selectedUserMasterId,
                allowedTabStatuses: allowed,
            }),
        })
            .then((res) => {
                if (!res.ok) {
                    return res.json().then((err) => {
                        throw new Error(
                            err.Message || err.message || 'Failed to save'
                        );
                    });
                }
                return res.json();
            })
            .then((data) => {
                const raw = Array.isArray(data?.allowedTabStatuses)
                    ? data.allowedTabStatuses
                    : allowed;
                const normalized = normalizeAllowedTabStatuses(raw);
                setAllowed(normalized);
                if (isSelectedCurrentUser) {
                    Actions.setUserInformation({
                        userId: Reducer.userInformation?.UserId,
                        userName: Reducer.userInformation?.UserName,
                        customerNumber: Reducer.userInformation?.CustomerNumber,
                        companyId: Reducer.userInformation?.CompanyId,
                        addressLineOne: Reducer.userInformation?.AddressLineOne,
                        addressLineTwo: Reducer.userInformation?.AddressLineTwo,
                        addressLineThree:
                            Reducer.userInformation?.AddressLineThree,
                        city: Reducer.userInformation?.City,
                        state: Reducer.userInformation?.State,
                        zip: Reducer.userInformation?.Zip,
                        country: Reducer.userInformation?.Country,
                        telephone: Reducer.userInformation?.Telephone,
                        fax: Reducer.userInformation?.Fax,
                        customerName: Reducer.userInformation?.CustomerName,
                        managerFlag: Reducer.userInformation?.ManagerFlag,
                        secondDayAirEnable:
                            Reducer.userInformation?.SecondDayAirEnable,
                        firstLogin: Reducer.userInformation?.FirstLogin,
                        repairCenterFlag:
                            Reducer.userInformation?.RepairCenterFlag,
                        allowedTabStatuses: normalized,
                    });
                }
                setSaved(true);
            })
            .catch((e) => setSaveError(e.message || 'Failed to save'))
            .finally(() => setSaving(false));
    }

    return (
        <div>
            <Form>
                <Form.Field>
                    <label>Target user</label>
                    <Dropdown
                        placeholder="Select user"
                        fluid
                        selection
                        options={userOptions}
                        value={selectedUserMasterId}
                        onChange={(e, data) =>
                            setSelectedUserMasterId(data.value)
                        }
                    />
                </Form.Field>
            </Form>

            {selectedUser ? (
                <Message>
                    <strong>Selected:</strong> {selectedUser.userName || '-'} (
                    {selectedUser.userId})
                </Message>
            ) : null}

            <div
                className="flex-justify-end"
                style={{ gap: 8, flexWrap: 'wrap' }}
            >
                <Button content="All" onClick={setAllAllowed} />
                <Button content="None" onClick={setNoneAllowed} />
                <Button content="Copy from me" onClick={copyFromLoginUser} />
                <Button
                    primary
                    content="Save"
                    onClick={save}
                    disabled={
                        selectedUserMasterId === '' ||
                        selectedUserMasterId == null
                    }
                />
            </div>

            {loadError ? <Message negative content={loadError} /> : null}
            {saveError ? <Message negative content={saveError} /> : null}
            {saved ? <Message positive content="Saved." /> : null}

            <Dimmer active={loading || saving} page>
                <Loader
                    content={saving ? 'Saving...' : 'Loading...'}
                    size="huge"
                />
            </Dimmer>

            <Divider />
            {groupToSelectors(
                baseGroups,
                allowedSet,
                onSingleLevelChange,
                onCombinedLevelChange
            )}
        </div>
    );
}
